import React, { useCallback, useEffect, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';

/**
 * 投稿画像の承認キュー（N-6）。
 *
 * **自動で通った画像もここに出る。** 自動審査はここに届く前のふるいであって、
 * ここの代わりではない —— 落とすのは「誰も見なくていい」ものだけで、
 * 残りは全部人が見る。
 *
 * 並びは 赤 → 迷い → 判定不能 → **未審査** → 通過。未審査が通過より前にあるのは、
 * **まだ誰も読んでいない画像は「きれい」ではない**から。上から順に処理する人が、
 * 機械が一度も見ていない画像を黙って公開してしまわないようにする。
 *
 * 承認の意味は投稿の種類で違う:
 * - 店舗写真 … 承認＝公開
 * - 口コミの添付 … 承認＝保留列から公開列に移す（本文はどちらでも残る）
 * - 景品報告の写真 … 却下＝写真だけ消す。報告の採否は景品のキューの仕事
 */
export default function ImageQueue() {
  useAuthGuard();
  const [items, setItems] = useState(null);
  const [unchecked, setUnchecked] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/image-queue`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setItems((body.data && body.data.items) || []);
      setUnchecked((body.data && body.data.unchecked) || 0);
    } catch (e) {
      setError('キューを読み込めませんでした: ' + e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (item, approve) => {
    const key = item.subjectKind + item.subjectId + item.imageUrl;
    setBusy(key);
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/image-queue/decide`, {
        method: 'POST',
        body: JSON.stringify({
          subjectKind: item.subjectKind,
          subjectId: item.subjectId,
          imageUrl: item.imageUrl,
          approve,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems((prev) => prev.filter((i) => i.subjectKind + i.subjectId + i.imageUrl !== key));
    } catch (e) {
      setError('反映できませんでした: ' + e.message);
    } finally {
      setBusy('');
    }
  };

  return (
    <Layout>
      <div className="container-fluid py-3">
        <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
          <h4 className="mb-0">投稿画像の承認</h4>
          {items !== null && (
            <span className="badge bg-primary-subtle text-primary-emphasis">{items.length}件</span>
          )}
          {unchecked > 0 && (
            <span className="badge bg-warning-subtle text-warning-emphasis">
              未審査 {unchecked}件（/check-images がまだ読んでいません）
            </span>
          )}
          <button className="btn btn-phoenix-secondary btn-sm" type="button" onClick={load}>
            再読み込み
          </button>
        </div>

        {error && <div className="alert alert-danger py-2">{error}</div>}
        {items === null && <div className="text-muted">読み込み中...</div>}
        {items !== null && items.length === 0 && (
          <div className="text-muted">承認待ちの画像はありません</div>
        )}

        <div className="row g-3">
          {(items || []).map((item) => (
            <ImageCard
              key={item.subjectKind + item.subjectId + item.imageUrl}
              item={item}
              busy={busy === item.subjectKind + item.subjectId + item.imageUrl}
              onDecide={decide}
            />
          ))}
        </div>
      </div>
    </Layout>
  );
}

const KIND_LABEL = {
  store_photo: '店舗写真',
  review: '口コミの添付',
  store_prize: '景品報告の写真',
};

// 判定の見た目。**未審査は灰色ではなく黄色** —— 「まだ読んでいない」は
// 「問題なし」ではない。
const VERDICT = {
  reject: { label: '自動却下', className: 'bg-danger text-white' },
  unsure: { label: '要確認', className: 'bg-warning-subtle text-warning-emphasis' },
  error: { label: '機械が読めなかった', className: 'bg-secondary-subtle text-secondary-emphasis' },
  ok: { label: '自動チェック通過', className: 'bg-success-subtle text-success-emphasis' },
};

function ImageCard({ item, busy, onDecide }) {
  const verdict = VERDICT[item.verdict] || {
    label: '未審査',
    className: 'bg-warning text-dark',
  };

  return (
    <div className="col-12 col-md-6 col-xl-4">
      <div className="card h-100">
        {/* 画像は原寸ではなく枠に収める。判定の材料は見えないと意味が無いので、
            クリックで元画像を開けるようにする */}
        <a href={item.imageUrl} target="_blank" rel="noreferrer">
          <img
            src={item.imageUrl}
            alt=""
            className="card-img-top"
            style={{ height: 220, objectFit: 'cover', background: '#f5f7fa' }}
            loading="lazy"
          />
        </a>
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <span className={`badge ${verdict.className}`}>{verdict.label}</span>
            <span className="badge bg-light text-secondary">{KIND_LABEL[item.subjectKind] || item.subjectKind}</span>
            {item.score != null && (
              <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                score {item.score.toFixed(2)}
              </span>
            )}
          </div>

          {(item.reasons || []).length > 0 && (
            <div className="mb-2">
              {item.reasons.map((r) => (
                <span key={r} className="badge bg-danger-subtle text-danger-emphasis me-1">
                  {r}
                </span>
              ))}
            </div>
          )}

          {/* モデルの一言。却下した理由を投稿者に返すときの元になる */}
          {item.note && (
            <div className="mb-2" style={{ fontSize: '0.8125rem', color: '#525b75' }}>
              {item.note}
            </div>
          )}

          <div className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>
            {item.storeName && (
              <>
                <a href={`/stores/${item.storeId}`}>{item.storeName}</a>
                {' / '}
              </>
            )}
            {item.submittedAt}
          </div>

          <div className="d-flex gap-2">
            <button
              className="btn btn-success btn-sm"
              type="button"
              disabled={busy}
              onClick={() => onDecide(item, true)}
            >
              公開する
            </button>
            <button
              className="btn btn-phoenix-secondary btn-sm"
              type="button"
              disabled={busy}
              onClick={() => onDecide(item, false)}
            >
              却下
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
